import { VALID_LANGUAGES, type Language } from "@/app/constants/legal";

// Framework-agnostic language + cookie helpers, safe to import anywhere
// (client, SSR entry, server controllers) without pulling in react-i18next.
//
// The chosen language is persisted in a cookie rather than localStorage because
// a cookie is sent with every request: the server reads it (entry.server.tsx)
// and the client reads it at init, so BOTH render the same language and there is
// no hydration mismatch — the bug that froze navigation for German users.

export const DEFAULT_LANGUAGE: Language = "en";
export const LANGUAGE_COOKIE = "lang";

// Narrow any value (cookie value, browser locale, DB language) to a supported
// language code, falling back to the default.
export function toSupportedLanguage(value?: string | null): Language {
  const base = (value ?? "").split("-")[0].toLowerCase();
  return (VALID_LANGUAGES as readonly string[]).includes(base)
    ? (base as Language)
    : DEFAULT_LANGUAGE;
}

// Read the persisted language from a cookie string — works for both a request
// "Cookie" header (server) and document.cookie (client).
export function getLanguageFromCookie(cookieHeader?: string | null): Language {
  const match = cookieHeader?.match(
    new RegExp(`(?:^|;\\s*)${LANGUAGE_COOKIE}=([^;]+)`)
  );
  return toSupportedLanguage(match?.[1]);
}

// `Set-Cookie` value used to seed the language server-side (e.g. on login).
export function languageCookieHeader(language?: string | null): string {
  return `${LANGUAGE_COOKIE}=${toSupportedLanguage(
    language
  )}; Path=/; Max-Age=31536000; SameSite=Lax`;
}

// Persist the chosen language client-side so the NEXT request renders it
// server-side and the two stay in sync.
export function setLanguageCookie(language: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${LANGUAGE_COOKIE}=${toSupportedLanguage(
    language
  )}; path=/; max-age=31536000; samesite=lax`;
}
